import fixture from "./fixtures/provider-observability.json";
import type {
  CapacityHealthState,
  CapacityPriorityTier,
  ProviderObservabilityDashboard
} from "./types";

const priorityOrder: Record<CapacityPriorityTier, number> = {
  FREE: 0,
  CODEX: 1,
  LOW_COST_PAID: 2,
  HUMAN_ASSISTED: 3
};

const blockerLabels: Record<string, string> = {
  AUTOMATIC_ROUTE_DISABLED: "Tự động định tuyến đang tắt",
  BENCHMARK_STALE: "Benchmark đã hết hạn",
  BENCHMARK_SAMPLE_INSUFFICIENT: "Chưa đủ mẫu benchmark",
  HEALTH_BELOW_THRESHOLD: "Health dưới ngưỡng",
  PROVIDER_QUOTA_EXHAUSTED: "Quota đã cạn",
  PROVIDER_CIRCUIT_OPEN: "Circuit breaker đang mở",
  QUALITY_BELOW_THRESHOLD: "Chất lượng dưới ngưỡng"
};

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function validateCapacityDashboard(value: unknown): ProviderObservabilityDashboard {
  if (!record(value) || value.schema !== "SYNERGY_PROVIDER_OBSERVABILITY_DASHBOARD_V1") {
    throw new Error("CAPACITY_FIXTURE_SCHEMA_INVALID");
  }
  if (value.environment !== "NONPRODUCTION_ONLY" || !Array.isArray(value.providers)) {
    throw new Error("CAPACITY_FIXTURE_BOUNDARY_INVALID");
  }
  if (!record(value.safety)) throw new Error("CAPACITY_FIXTURE_SAFETY_MISSING");
  const safety = value.safety;
  if (
    safety.provider_calls !== 0 ||
    safety.provider_spend_usd !== 0 ||
    safety.secret_value_access !== false ||
    safety.raw_prompt_storage !== false ||
    safety.raw_customer_data_storage !== false ||
    safety.chain_of_thought_storage !== false
  ) {
    throw new Error("CAPACITY_FIXTURE_SAFETY_INVALID");
  }

  const identities = new Set<string>();
  for (const provider of value.providers) {
    if (!record(provider) || provider.schema !== "SYNERGY_PROVIDER_OBSERVABILITY_VIEW_V1") {
      throw new Error("CAPACITY_PROVIDER_SCHEMA_INVALID");
    }
    if (
      typeof provider.provider_id !== "string" ||
      typeof provider.model_id !== "string" ||
      !provider.provider_id.startsWith("synthetic-") ||
      !provider.model_id.startsWith("synthetic-")
    ) {
      throw new Error("CAPACITY_PROVIDER_NOT_SYNTHETIC");
    }
    const identity = `${provider.provider_id}/${provider.model_id}`;
    if (identities.has(identity)) throw new Error("CAPACITY_PROVIDER_DUPLICATE");
    identities.add(identity);

    if (!record(provider.benchmark) || provider.benchmark.input_scope !== "SYNTHETIC") {
      throw new Error("CAPACITY_BENCHMARK_SCOPE_INVALID");
    }
    if (!record(provider.eligibility)) throw new Error("CAPACITY_ELIGIBILITY_MISSING");
    if (
      provider.eligibility.route_eligible !== false ||
      provider.eligibility.automatic_route_allowed !== false
    ) {
      throw new Error("CAPACITY_OFFLINE_ROUTE_MUST_BE_DISABLED");
    }
  }

  if (!record(value.summary)) throw new Error("CAPACITY_FIXTURE_SUMMARY_MISSING");
  if (
    value.summary.provider_model_count !== value.providers.length ||
    value.summary.eligible_count !== 0
  ) {
    throw new Error("CAPACITY_FIXTURE_SUMMARY_INVALID");
  }

  return value as unknown as ProviderObservabilityDashboard;
}

export const capacityDashboard = validateCapacityDashboard(fixture);

export function scoreTone(score: number): "good" | "warn" | "bad" {
  if (score >= 85) return "good";
  if (score >= 70) return "warn";
  return "bad";
}

export function blockerLabel(code: string): string {
  return blockerLabels[code] ?? code.replaceAll("_", " ");
}

export function priorityLabel(tier: CapacityPriorityTier): string {
  return {
    FREE: "Miễn phí",
    CODEX: "Codex quota",
    LOW_COST_PAID: "Paid chi phí thấp",
    HUMAN_ASSISTED: "Con người hỗ trợ"
  }[tier];
}

export function assertDeterministicProviderOrder(
  dashboard: ProviderObservabilityDashboard
): void {
  const priorities = dashboard.providers.map((provider) => priorityOrder[provider.priority_tier]);
  for (let index = 1; index < priorities.length; index += 1) {
    if (priorities[index] < priorities[index - 1]) {
      throw new Error("CAPACITY_PROVIDER_ORDER_INVALID");
    }
  }
}

export function healthCount(
  dashboard: ProviderObservabilityDashboard,
  state: CapacityHealthState
): number {
  return dashboard.summary.health_state_counts[state] ?? 0;
}

assertDeterministicProviderOrder(capacityDashboard);
